import React, { useState, useRef } from "react";
import { useNavigate, useLocation, Navigate, Link } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "../lib/auth";
import {
  Briefcase, Brain, Compass, Rocket, ArrowRight, CheckCircle,
  Lightbulb, ChartBar, Target, Handshake, FileText, ChatCircleDots,
  LinkedinLogo, MagnifyingGlass, ShieldCheck, MapPin, ArrowUpRight,
  Users, Sparkle, Lock,
} from "@phosphor-icons/react";
import CookieNotice from "../components/CookieNotice";

// --- Static content data ----------------------------------------------------

const PILLARS = [
  {
    color: "#1E88E5",
    label: "Zelfinzicht",
    kicker: "Wie ben je?",
    title: "Ken je eigen brein.",
    body:
      "Breintype-test, IQ-test, competentietest en conflicthanteringstest. " +
      "Krijg een helder beeld van jouw aanleg, valkuilen en denkstijl — " +
      "gebaseerd op wetenschappelijke modellen.",
    icon: Brain,
  },
  {
    color: "#E53935",
    label: "Verkenning",
    kicker: "Wat past bij je?",
    title: "Ontdek jouw richting.",
    body:
      "Werkwaarden, loopbaanwaarden en de beroepentest brengen jouw " +
      "drijfveren in kaart. Van duizenden beroepen filteren we samen " +
      "wat écht bij je past.",
    icon: Compass,
  },
  {
    color: "#43A047",
    label: "Actie",
    kicker: "Hoe kom je er?",
    title: "Zet stappen die werken.",
    body:
      "Van een sterk CV en persoonlijke sollicitatiebrieven tot een " +
      "LinkedIn-plan, STARR-interviewtraining en actuele vacatures — " +
      "alles wat je nodig hebt om de sprong te maken.",
    icon: Rocket,
  },
];

const FEATURES = [
  { icon: Brain,          title: "Breintype-test",         body: "90 vragen, gebaseerd op Panksepp & het Pad-bij-Aanleg model." },
  { icon: Lightbulb,      title: "Klassieke IQ-test",      body: "Adaptieve items met betrouwbaar 95% betrouwbaarheidsinterval." },
  { icon: ChartBar,       title: "Werkwaardetest",         body: "Ontdek jouw top-10 waarden via paarsgewijze vergelijking." },
  { icon: Compass,        title: "Loopbaanwaardetest",     body: "Welke koers past bij jouw kernwaarden in het werk?" },
  { icon: Target,         title: "Competentietest",        body: "Zelfscore + 360°-feedback van collega's en leidinggevenden." },
  { icon: Briefcase,      title: "Beroepentest",           body: "AI-match op duizenden beroepen op basis van jouw profiel." },
  { icon: Handshake,      title: "Conflicthantering",      body: "21 dilemma's — op maat van jouw breintype en functie." },
  { icon: FileText,       title: "Slimme CV-bouwer",       body: "Importeer je oude CV, laat de AI het herschrijven en polijst het." },
  { icon: ChatCircleDots, title: "STARR-interviewcoach",   body: "Oefen sollicitatiegesprekken met een AI-recruiter, 24/7." },
  { icon: LinkedinLogo,   title: "LinkedIn-stappenplan",   body: "Persoonlijk plan om je profiel én je netwerk sterker te maken." },
  { icon: MagnifyingGlass,title: "Vacatures op maat",      body: "Actuele banen die aansluiten bij jouw zoekprofiel — dagelijks." },
  { icon: ShieldCheck,    title: "AVG-veilig",             body: "Alleen jij en je work coach zien je data. Volledig GDPR-proof." },
];

const STEPS = [
  { n: "01", title: "Kies een work coach",     body: "Neem contact op met een van onze aangesloten work coaches. Zij nodigen je uit op het platform." },
  { n: "02", title: "Doe de tests",           body: "Werk in je eigen tempo tests en profielen af. Alles wordt automatisch opgeslagen." },
  { n: "03", title: "Ontvang jouw profielen", body: "AI stelt persoonlijke rapporten op — over jouw brein, waarden en kansen op de arbeidsmarkt." },
  { n: "04", title: "Solliciteer met lef",    body: "CV, brief, LinkedIn, interviews en vacatures — alle tools op één plek. Jouw coach kijkt mee." },
];

const AUDIENCES = [
  { title: "Werkzoekenden",       body: "Weer richting geven aan je zoektocht en met een sterker verhaal solliciteren." },
  { title: "Werkenden met vragen",body: "Twijfels over je huidige baan? Ontdek of het aan de rol ligt — of aan jou." },
  { title: "Herstartende talenten", body: "Terug na burnout, ziekte of pauze. In kleine stappen zicht op wat weer past." },
  { title: "Loopbaanswitchers",   body: "Denk je aan een nieuwe branche of vak? Ontdek waar jouw brein floreert." },
];

const COACHES = [
  {
    name: "Joost van der Leij",
    region: "Regio Amsterdam",
    url: "https://www.joostvanderleij.nl/",
    body:
      "Grondlegger van de breintype-methodiek en auteur van meerdere boeken " +
      "over persoonlijkheid, brein en carrière. Coacht kritische denkers en " +
      "professionals die dieper willen graven.",
    color: "#1E88E5",
    accent: "Amsterdam & Randstad",
  },
  {
    name: "Wendie den Doop",
    region: "Regio Eindhoven",
    url: "https://dendooparbeidscoaching.nl/",
    body:
      "Arbeidscoach met jarenlange ervaring in re-integratie, herstel en " +
      "loopbaanbegeleiding. Warm, direct en resultaatgericht — voor wie " +
      "opnieuw wil bouwen aan werk dat klopt.",
    color: "#43A047",
    accent: "Eindhoven & Brabant",
  },
];

// --- Component --------------------------------------------------------------

export default function LoginPage() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname;
  const loginRef = useRef(null);
  const coachesRef = useRef(null);

  if (user && typeof user === "object") {
    if (user.role === "admin") return <Navigate to={from || "/admin"} replace />;
    if (user.role === "job_coach") return <Navigate to={from || "/dashboard"} replace />;
    return <Navigate to="/tests" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const u = await login(email.trim(), password);
      if (u.role === "admin") navigate(from || "/admin");
      else if (u.role === "job_coach") navigate(from || "/dashboard");
      else navigate("/tests");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const scrollTo = (ref) => (e) => {
    e.preventDefault();
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid="landing-page">
      <CookieNotice />

      {/* --- Sticky nav ------------------------------------------------- */}
      <nav
        className="sticky top-0 z-30 bg-white/85 backdrop-blur-md border-b border-zinc-200"
        data-testid="landing-nav"
      >
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Briefcase size={26} weight="duotone" color="#1E88E5" />
            <div className="leading-tight">
              <div className="font-display text-base font-bold tracking-tight">Werk Dat Past Online</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Coaching platform</div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <a
              href="#coaches"
              onClick={scrollTo(coachesRef)}
              data-testid="nav-cta-coach"
              className="hidden sm:inline-flex items-center gap-1 text-xs uppercase tracking-widest text-zinc-600 hover:text-zinc-900 transition-colors"
            >
              Vind een coach
            </a>
            <a
              href="#login"
              onClick={scrollTo(loginRef)}
              data-testid="nav-cta-login"
              className="inline-flex items-center gap-1.5 bg-zinc-900 text-white px-4 py-2 text-xs font-semibold tracking-widest uppercase rounded-sm hover:bg-[#1E88E5] transition-colors"
            >
              Inloggen <ArrowRight size={12} weight="bold" />
            </a>
          </div>
        </div>
      </nav>

      {/* --- Hero ------------------------------------------------------- */}
      <section
        className="relative overflow-hidden border-b border-zinc-200 bg-[var(--c-bg)]"
        data-testid="hero-section"
      >
        {/* Decorative dot grid */}
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle, #18181b 1px, transparent 1px)",
            backgroundSize: "24px 24px",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-6 pt-20 pb-24 lg:pt-28 lg:pb-32 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 fade-up">
            <div className="flex items-center gap-2 mb-6">
              <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#1E88E5]" />
              <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#E53935]" />
              <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#43A047]" />
              <span className="ml-2 text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                Voor werkzoekenden & switchers
              </span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-[0.95] tracking-tight max-w-4xl">
              Ontdek jouw brein.
              <br />
              <span className="text-[#1E88E5]">Bouw</span>{" "}
              jouw loopbaan.
            </h1>
            <p className="mt-8 text-lg sm:text-xl text-zinc-700 leading-relaxed max-w-2xl">
              Werk Dat Past Online combineert wetenschappelijke tests, AI-analyse en
              persoonlijke coaching. Voor iedereen die weer richting wil geven
              aan werk — op basis van wie je écht bent.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <a
                href="#coaches"
                onClick={scrollTo(coachesRef)}
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 bg-zinc-900 text-white px-6 py-3.5 text-sm font-semibold tracking-widest uppercase rounded-sm hover:bg-[#1E88E5] transition-colors"
              >
                Start met een work coach <ArrowRight size={16} weight="bold" />
              </a>
              <a
                href="#login"
                onClick={scrollTo(loginRef)}
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 bg-white text-zinc-900 border-2 border-zinc-900 px-6 py-3.5 text-sm font-semibold tracking-widest uppercase rounded-sm hover:bg-zinc-900 hover:text-white transition-colors"
              >
                Al toegang? Inloggen
              </a>
            </div>
            <div className="mt-10 flex items-center gap-6 text-xs uppercase tracking-widest text-zinc-500">
              <span className="flex items-center gap-1.5">
                <ShieldCheck size={14} weight="duotone" /> AVG-veilig
              </span>
              <span className="flex items-center gap-1.5">
                <Sparkle size={14} weight="duotone" /> AI-gedreven
              </span>
              <span className="flex items-center gap-1.5">
                <Users size={14} weight="duotone" /> Met echte coaches
              </span>
            </div>
          </div>

          {/* Hero side card — sample zoekprofiel summary */}
          <div className="lg:col-span-4 flex items-center justify-center fade-up">
            <div className="neuro-card-bold w-full max-w-sm bg-white p-6 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.15)]">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                  Jouw zoekprofiel
                </div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#43A047] flex items-center gap-1">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#43A047]" /> Actief
                </div>
              </div>

              <div className="mt-3 font-display text-2xl font-bold tracking-tight leading-[1.1]">
                Docent techniek
                <span className="block text-sm font-normal text-zinc-500 mt-0.5">
                  MBO / VMBO — Eindhoven e.o.
                </span>
              </div>

              <div className="mt-5 space-y-3 text-sm">
                <ProfileRow label="Uren" value="24–32 per week" color="#1E88E5" />
                <ProfileRow label="Startbelasting" value="Halve dagen, opbouwend" color="#1E88E5" />
                <ProfileRow label="Actieradius" value="30 km, auto" color="#1E88E5" />
                <ProfileRow label="Branches" value="Onderwijs, techniek, industrie" color="#E53935" />
                <ProfileRow label="Top-3 waarden" value="Zingeving · Autonomie · Rust" color="#43A047" />
              </div>

              <div className="mt-5 pt-5 border-t border-zinc-200">
                <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">
                  <span>Volledigheid</span>
                  <span className="tabular-nums text-zinc-900 font-semibold">24 / 28</span>
                </div>
                <div className="h-1.5 bg-zinc-100 rounded-sm overflow-hidden">
                  <div className="h-full bg-[#1E88E5] transition-all" style={{ width: "86%" }} />
                </div>
                <p className="mt-3 text-xs text-zinc-500 leading-relaxed">
                  Je zoekprofiel bundelt alles wat je kunt, wilt en aankunt.
                  De AI vult vast in — jij en je coach verfijnen.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- 3 Pillars -------------------------------------------------- */}
      <section className="border-b border-zinc-200" data-testid="pillars-section">
        <div className="max-w-6xl mx-auto px-6 py-20 lg:py-28">
          <div className="max-w-2xl mb-14">
            <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 mb-3">Drie pijlers</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1]">
              Van zelfinzicht naar de juiste stap.
            </h2>
            <p className="mt-5 text-zinc-600 leading-relaxed text-lg">
              Geen losse testjes, maar één samenhangend traject.
              Elke stap bouwt logisch op de vorige — met jouw coach als klankbord.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {PILLARS.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.label}
                  className="neuro-card-bold bg-white p-7 flex flex-col gap-4 hover:-translate-y-1 transition-transform"
                  data-testid={`pillar-${p.label.toLowerCase()}`}
                >
                  <div
                    className="w-12 h-12 flex items-center justify-center rounded-sm"
                    style={{ background: `${p.color}15`, color: p.color }}
                  >
                    <Icon size={26} weight="duotone" />
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: p.color }}>
                    {p.kicker} — {p.label}
                  </div>
                  <h3 className="font-display text-2xl font-bold tracking-tight leading-[1.1]">
                    {p.title}
                  </h3>
                  <p className="text-sm text-zinc-600 leading-relaxed">{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* --- Features grid --------------------------------------------- */}
      <section className="border-b border-zinc-200 bg-[var(--c-bg)]" data-testid="features-section">
        <div className="max-w-6xl mx-auto px-6 py-20 lg:py-28">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 mb-3">Wat je krijgt</div>
              <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1]">
                Twaalf tools. Eén platform.
              </h2>
            </div>
            <p className="text-zinc-600 leading-relaxed max-w-md">
              Alles wat je nodig hebt — van zelfonderzoek tot solliciteren —
              in één samenhangende omgeving. Toegankelijk via je work coach.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((f, idx) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.title}
                  className="group bg-white border border-zinc-200 hover:border-zinc-900 p-5 rounded-sm transition-colors flex gap-4"
                  data-testid={`feature-${idx}`}
                >
                  <div className="shrink-0 w-10 h-10 flex items-center justify-center bg-zinc-100 group-hover:bg-[#1E88E5]/10 group-hover:text-[#1E88E5] transition-colors rounded-sm">
                    <Icon size={20} weight="duotone" />
                  </div>
                  <div>
                    <h3 className="font-display text-base font-bold tracking-tight">{f.title}</h3>
                    <p className="text-xs text-zinc-600 mt-1 leading-relaxed">{f.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* --- How it works ---------------------------------------------- */}
      <section className="border-b border-zinc-200" data-testid="how-section">
        <div className="max-w-6xl mx-auto px-6 py-20 lg:py-28">
          <div className="max-w-2xl mb-14">
            <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 mb-3">Hoe het werkt</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1]">
              In vier stappen op weg.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, idx) => (
              <div
                key={s.n}
                className="relative p-6 border-t-2 border-zinc-900"
                data-testid={`step-${idx}`}
              >
                <div className="font-display text-6xl font-bold text-zinc-200 leading-none tracking-tight">
                  {s.n}
                </div>
                <h3 className="mt-4 font-display text-xl font-bold tracking-tight">{s.title}</h3>
                <p className="mt-2 text-sm text-zinc-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- Audiences ------------------------------------------------- */}
      <section className="border-b border-zinc-200 bg-zinc-900 text-white" data-testid="audience-section">
        <div className="max-w-6xl mx-auto px-6 py-20 lg:py-28">
          <div className="max-w-2xl mb-14">
            <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-400 mb-3">Voor wie</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1]">
              Als je merkt dat het niet meer klopt, is dit het moment.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {AUDIENCES.map((a) => (
              <div
                key={a.title}
                className="p-5 bg-white/5 border border-white/10 hover:border-white/40 rounded-sm transition-colors"
                data-testid={`audience-${a.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <CheckCircle size={22} weight="duotone" className="text-[#43A047] mb-3" />
                <h3 className="font-display text-lg font-bold tracking-tight">{a.title}</h3>
                <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{a.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- Coach CTA ------------------------------------------------- */}
      <section
        ref={coachesRef}
        id="coaches"
        className="border-b border-zinc-200 bg-white"
        data-testid="coaches-section"
      >
        <div className="max-w-6xl mx-auto px-6 py-20 lg:py-28">
          <div className="max-w-2xl mb-14">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#1E88E5] mb-3">Neem contact op</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1]">
              Kies jouw work coach.
            </h2>
            <p className="mt-5 text-zinc-600 leading-relaxed text-lg">
              Toegang tot Werk Dat Past Online loopt via één van onze aangesloten
              coaches. Zij begeleiden je persoonlijk en nodigen je uit op het
              platform.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {COACHES.map((c) => (
              <a
                key={c.name}
                href={c.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`coach-card-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="group neuro-card-bold bg-white p-8 flex flex-col gap-5 hover:-translate-y-1 hover:shadow-[0_20px_60px_-15px_rgba(0,0,0,0.2)] transition-all"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div
                      className="text-[10px] uppercase tracking-[0.22em] flex items-center gap-1.5"
                      style={{ color: c.color }}
                    >
                      <MapPin size={12} weight="fill" /> {c.accent}
                    </div>
                    <h3 className="mt-2 font-display text-3xl font-bold tracking-tight leading-[1.05]">
                      {c.name}
                    </h3>
                    <div className="mt-1 text-sm text-zinc-500">{c.region}</div>
                  </div>
                  <div
                    className="shrink-0 w-11 h-11 flex items-center justify-center rounded-sm border border-zinc-200 group-hover:border-zinc-900 group-hover:bg-zinc-900 group-hover:text-white transition-colors"
                    aria-hidden="true"
                  >
                    <ArrowUpRight size={20} weight="bold" />
                  </div>
                </div>
                <p className="text-sm text-zinc-600 leading-relaxed">{c.body}</p>
                <div className="mt-auto pt-4 border-t border-zinc-200 flex items-center justify-between">
                  <span className="text-xs uppercase tracking-widest text-zinc-500 truncate">
                    {c.url.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                  </span>
                  <span
                    className="text-xs font-semibold uppercase tracking-widest group-hover:underline"
                    style={{ color: c.color }}
                  >
                    Neem contact op →
                  </span>
                </div>
              </a>
            ))}
          </div>
          <div className="mt-10 text-center text-sm text-zinc-500">
            Ben je zelf coach en wil je aansluiten?{" "}
            <a
              href="mailto:joost@tiouw.com"
              className="underline hover:text-zinc-900"
              data-testid="coach-onboarding-link"
            >
              joost@tiouw.com
            </a>
          </div>
        </div>
      </section>

      {/* --- Login section --------------------------------------------- */}
      <section
        ref={loginRef}
        id="login"
        className="border-b border-zinc-200 bg-[var(--c-bg)]"
        data-testid="login-section"
      >
        <div className="max-w-6xl mx-auto px-6 py-20 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 mb-3">Al toegang?</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1]">
              Log in en pak de draad weer op.
            </h2>
            <p className="mt-5 text-zinc-600 leading-relaxed text-lg max-w-md">
              Je work coach heeft je uitgenodigd? Log hiernaast in met de
              gegevens die je hebt ontvangen. Je voortgang wordt automatisch
              bewaard.
            </p>
            <div className="mt-8 flex items-center gap-3 text-xs uppercase tracking-widest text-zinc-500">
              <Lock size={14} weight="duotone" />
              <span>Beveiligd met AVG-conforme sessies</span>
            </div>
          </div>

          <div className="w-full max-w-md justify-self-center lg:justify-self-end">
            <div className="neuro-card-bold bg-white p-8 sm:p-10 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.15)]">
              <h3 className="font-display text-2xl font-bold tracking-tight">Inloggen</h3>
              <p className="text-sm text-zinc-500 mt-1">Welkom terug. Vul je gegevens in.</p>

              <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="login-form">
                <div>
                  <label className="text-xs uppercase tracking-widest text-zinc-500 block mb-1.5">E-mailadres</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="login-email-input"
                    className="w-full border border-zinc-300 focus:border-zinc-900 outline-none px-3 py-2.5 text-[15px] rounded-sm bg-white transition-colors"
                    placeholder="naam@bedrijf.nl"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-zinc-500 block mb-1.5">Wachtwoord</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="login-password-input"
                    className="w-full border border-zinc-300 focus:border-zinc-900 outline-none px-3 py-2.5 text-[15px] rounded-sm bg-white transition-colors"
                    placeholder="••••••••"
                  />
                </div>

                {error && (
                  <div data-testid="login-error" className="text-sm text-[#E53935] border border-[#E53935]/30 bg-[#E53935]/5 px-3 py-2 rounded-sm">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="login-submit-button"
                  className="w-full bg-zinc-900 text-white py-3 text-sm font-semibold tracking-widest uppercase rounded-sm hover:bg-[#1E88E5] transition-colors disabled:opacity-60"
                >
                  {submitting ? "Bezig met inloggen..." : "Inloggen"}
                </button>
              </form>

              <div className="mt-6 pt-6 border-t border-zinc-200 text-xs text-zinc-500 leading-relaxed">
                Nog geen account?{" "}
                <a
                  href="#coaches"
                  onClick={scrollTo(coachesRef)}
                  className="underline hover:text-zinc-900"
                  data-testid="login-to-coaches-link"
                >
                  Neem contact op met een work coach
                </a>{" "}
                — zij nodigen je uit.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- Footer ---------------------------------------------------- */}
      <footer className="bg-white" data-testid="landing-footer">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <Briefcase size={22} weight="duotone" color="#1E88E5" />
            <div className="leading-tight">
              <div className="font-display text-sm font-bold tracking-tight">Werk Dat Past Online</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Coaching platform</div>
            </div>
          </div>
          <div className="text-xs text-zinc-500 leading-relaxed" data-testid="copyright-notice">
            © {new Date().getFullYear()} TIOUW.com BV — Binderij 7-L, 1185 ZH Amstelveen, Nederland — KvK 05076640
            {" · "}
            <Link to="/privacy" className="underline hover:text-zinc-900 transition-colors" data-testid="login-privacy-link">
              Privacyverklaring
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

// --- Small subcomponents ----------------------------------------------------

function ProfileRow({ label, value, color }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-[11px] uppercase tracking-widest text-zinc-500 shrink-0 pt-0.5">
        {label}
      </span>
      <span
        className="text-right text-[13px] leading-snug font-medium"
        style={{ color }}
      >
        {value}
      </span>
    </div>
  );
}


